import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useHelpArticles } from '../../hooks/useHelpArticles';
import { useHelpCentre } from '../../contexts/HelpCentreContext';
import { HELP_CATEGORIES, pickLang, searchArticles } from '../../utils/helpCentre';
import HelpArticleView from './HelpArticleView';

export default function HelpBrowse({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  const { articles, loading, error } = useHelpArticles();
  const { articleId, setArticleId } = useHelpCentre();
  const [term, setTerm] = useState('');

  const groups = useMemo(() => {
    const sortKey = (a) => [a.order ?? 999, pickLang(a.title, lang)];
    const sorted = [...articles].sort((x, y) => {
      const [ox, tx] = sortKey(x);
      const [oy, ty] = sortKey(y);
      return ox - oy || tx.localeCompare(ty);
    });
    return HELP_CATEGORIES
      .map((c) => ({ ...c, items: sorted.filter((a) => (a.category || 'gettingStarted') === c.key) }))
      .filter((g) => g.items.length);
  }, [articles, lang]);

  const results = useMemo(() => (term.trim() ? searchArticles(articles, term, lang) : null), [articles, term, lang]);
  const article = articleId ? articles.find((a) => a.id === articleId) : null;

  if (article) {
    return <HelpArticleView article={article} lang={lang} onBack={() => setArticleId(null)} onNavigate={onNavigate} />;
  }

  const Row = ({ a }) => (
    <button
      type="button" onClick={() => setArticleId(a.id)}
      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50"
    >
      <span className="min-w-0">
        <span className="block text-sm text-gray-900 truncate">{pickLang(a.title, lang)}</span>
        {results && pickLang(a.summary, lang) && <span className="block text-xs text-gray-500 truncate">{pickLang(a.summary, lang)}</span>}
      </span>
      <ChevronRightIcon className="h-4 w-4 text-gray-300 flex-shrink-0" />
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="relative">
        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="search" value={term} onChange={(e) => setTerm(e.target.value)}
          placeholder={t('admin:help.browse.search', 'Search the guides')}
          className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>
      {loading && <p className="text-sm text-gray-400">{t('common:actions.loading', 'Loading…')}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {results && (
        results.length
          ? <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 overflow-hidden">{results.map((a) => <Row key={a.id} a={a} />)}</div>
          : <p className="text-sm text-gray-500">{t('admin:help.browse.noResults', 'No guides match that. Try asking the question in the Ask tab instead.')}</p>
      )}
      {!results && groups.map((g) => (
        <div key={g.key}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{t(`admin:help.categories.${g.key}`, g.label)}</h3>
          <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 overflow-hidden">
            {g.items.map((a) => <Row key={a.id} a={a} />)}
          </div>
        </div>
      ))}
      {!loading && !error && !results && groups.length === 0 && (
        <p className="text-sm text-gray-500">{t('admin:help.browse.empty', 'No guides yet.')}</p>
      )}
    </div>
  );
}
